"use client";
import "@/styles/aboutOneContent.style.css";
import useLocalizer from "@/hooks/useLocalizer";
import SectionTitle from "@/components/shared/sectionTitle";
import { cn, formatPhoneNumber, getFilePath } from "@/lib/utils/utils";
import { PhoneCall } from "lucide-react";
import { HoverButton } from "@/components/shared/hoverButton";
import { Link } from "@/i18n/routing";
import HeaderItem from "../header/headerItem";
import Image from "next/image";
import Text from "@/components/shared/text";
import Flex from "@/components/core/flex";
import { About } from "@/types/home";
import DynamicIcon from "@/components/shared/dynamicIcon";
import { useInfo } from "@/components/shared/infoProvider";
import { TagSize } from "@/types/core";

interface Props {
  data: About;
}
const AboutOneContent = ({ data }: Props) => {
  const aboutUs = data;
  const { isRtl, t } = useLocalizer();
  const { data: clientInfo } = useInfo();
  return (
    <section className="about-one">
      <div
        className="about-one-shape float-bob-x"
        data-dir={isRtl ? "rtl" : "ltr"}
      />
      <div
        className="about-one-wall data-[dir=rtl]:-left-25 data-[dir=ltr]:-right-25"
        style={{
          backgroundImage: "url('/images/backgrounds/wall.png')",
        }}
        data-dir={isRtl ? "rtl" : "ltr"}
      />
      <div className="container mx-auto px-6 lg:px-0">
        <div className="grid lg:grid-cols-2 grid-cols-1 items-center">
          <Flex direction="col" spacing={{ all: 14 }} className="px-6">
            <SectionTitle
              title={
                isRtl ? (aboutUs?.arTitle ?? "") : (aboutUs?.enTitle ?? "")
              }
              titleTag="h2"
              description={
                isRtl
                  ? (aboutUs?.arSubTitle ?? "")
                  : (aboutUs?.enSubTitle ?? "")
              }
              descriptionTag="h3"
            />
            <Text as="p">
              {isRtl
                ? (aboutUs?.arDescription ?? "")
                : (aboutUs?.enDescription ?? "")}
            </Text>
            <Flex
              direction="row"
              flexWrap="wrap"
              spacing={{
                all: 4,
              }}
            >
              {aboutUs?.attributes?.map((attribute) => (
                <AboutUsFeatureCard
                  key={attribute.id}
                  title={isRtl ? attribute.arName : attribute.enName}
                  iconName={attribute?.icon?.code ?? ""}
                  iconClassName={attribute?.icon?.classes ?? ""}
                />
              ))}
            </Flex>
            <Flex direction="row" flexWrap="wrap" spacing={{ all: 4 }}>
              <Link href={"/aboutUs"}>
                <HoverButton
                  variant="secondary"
                  className="h-14 min-w-48  text-white"
                >
                  {t("buttons.discoverMore")}
                </HoverButton>
              </Link>
              <HeaderItem
                icon={PhoneCall}
                title={t("titles.callRightNow")}
                titleClassName="text-secondary-foreground text-lg"
                item={
                  <Link href={`tel:${clientInfo?.info?.mobileNumber}`}>
                    {formatPhoneNumber(
                      clientInfo?.info?.mobileNumber ?? "",
                      "+(XXX)-XXXXXXXXX",
                    )}
                  </Link>
                }
                itemClassName="hover:text-primary text-xl lg:text-2xl"
                iconClassName="rounded-full bg-primary w-12 h-12 flex items-center justify-center text-white hover:bg-secondary transition-all duration-300 ease-in-out"
              />
            </Flex>
          </Flex>
          <div className="relative w-full h-full flex justify-center">
            <div className="relative w-full lg:max-w-xl lg:h-full h-[650px] mt-22 mb-20">
              {aboutUs?.bigImage && (
                <Image
                  alt={
                    isRtl ? (aboutUs?.arTitle ?? "") : (aboutUs?.enTitle ?? "")
                  }
                  src={getFilePath(aboutUs?.bigImage?.key ?? "")}
                  fill
                  className={cn(
                    "object-fill absolute",
                    isRtl
                      ? "rounded-tl-[70px] rounded-br-[70px]"
                      : "rounded-tr-[70px] rounded-bl-[70px]",
                  )}
                  priority
                  placeholder="empty"
                />
              )}
              {aboutUs?.showWidget && (
                <div
                  className={cn(
                    "absolute -bottom-16  h-64 w-48 bg-primary rounded-b-[70px] flex flex-col justify-center ",
                    isRtl
                      ? "-left-3 lg:-left-16 rounded-tl-[70px]"
                      : "-right-3 lg:-right-16 rounded-tr-[70px]",
                  )}
                >
                  <Text
                    as="span"
                    className={cn("flex items-center px-5 justify-start")}
                  >
                    <DynamicIcon
                      name={aboutUs?.widgetIcon?.code ?? ""}
                      size={80}
                      className={cn(
                        "text-white",
                        aboutUs?.widgetIcon?.classes ?? "",
                      )}
                    />
                  </Text>
                  <Flex
                    center
                    direction="col"
                    spacing={{ all: 1 }}
                    className="mt-5"
                  >
                    <Text className="text-white text-5xl font-bold">
                      {aboutUs?.widgetValue ?? ""}
                    </Text>
                    <Text className="text-white text-lg">
                      {isRtl ? aboutUs?.arWidgetTitle : aboutUs?.enWidgetTitle}
                    </Text>
                  </Flex>
                </div>
              )}
            </div>
            <div
              className={cn(
                "absolute  top-40 -z-1 hidden xl:block",
                isRtl ? "-left-12" : "-right-12",
              )}
            >
              {aboutUs?.smallImage && (
                <div className="relative w-60 h-80">
                  <Image
                    alt="about"
                    src={getFilePath(aboutUs?.smallImage?.key ?? "")}
                    fill
                    priority
                    placeholder="empty"
                    className={cn(
                      "object-cover",
                      isRtl
                        ? "rounded-tl-[70px] rounded-br-[70px]"
                        : "rounded-tr-[70px] rounded-bl-[70px]",
                    )}
                  />
                </div>
              )}
            </div>
            <div
              className={cn(
                "about-one-wall-shape animate-[bounce_5s_linear_infinite]",
                isRtl
                  ? "rounded-tl-[70px] rounded-br-[70px] xl:right-8 -right-5"
                  : "rounded-tr-[70px] rounded-bl-[70px] xl:left-8 -left-5",
              )}
            />
            <div
              className={cn(
                "absolute -bottom-16  shape-mover -z-1 hidden lg:block",
                isRtl ? "right-12" : "left-12",
              )}
            >
              <div className={cn("relative h-60 w-16")}>
                <Image
                  src="/images/dots.png"
                  alt={"dots"}
                  fill
                  priority
                  placeholder="empty"
                  className="object-cover opacity-20 "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface AboutUsFeatureCardProps extends React.ComponentProps<"article"> {
  iconName: string;
  iconClassName?: string;
  title: string;
  as?: TagSize;
}

export const AboutUsFeatureCard: React.FC<AboutUsFeatureCardProps> = ({
  iconName,
  title,
  iconClassName,
  as = "p",
}) => {
  const { isRtl } = useLocalizer();
  return (
    <article
      className={cn(
        "relative  basis-11/12  lg:basis-52 min-h-24 border border-primary flex flex-row gap-2 items-center overflow-hidden justify-baseline p-4 hover:[&_.fe-cover]:w-full transition-all duration-300 ease-in-out hover:[&_.fe-icon]:text-white hover:[&>p]:text-white",
        isRtl
          ? "rounded-tl-full rounded-bl-full"
          : "rounded-tr-full rounded-br-full",
      )}
    >
      <div className="absolute inset-0 bg-primary h-full w-1" />
      <div
        className={cn(
          "fe-cover absolute inset-0 bg-primary h-full w-0  -z-1 transition-all duration-300 ease-in-out",
          isRtl ? "right-0" : "left-0",
        )}
      />
      <DynamicIcon
        size={42}
        className={cn(
          "fe-icon transition-all duration-300 ease-in-out text-primary",
          iconClassName,
        )}
        name={iconName}
      />
      <Text
        as={as}
        className="text-secondary text-lg transition-all duration-300 ease-in-out"
      >
        {title}
      </Text>
    </article>
  );
};

export default AboutOneContent;
